import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { AuthService } from '@auth0/auth0-angular';
import { Store } from '@ngrx/store';
import { GraphQLError } from 'graphql';
import { environment } from 'projects/semeris/src/environments/environment';
import { AppActions } from 'projects/semeris/src/store/app/app.actions';
import { RootState } from 'projects/semeris/src/store/root-state.model';
import { Observable, throwError, TimeoutError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

import {
  FAIL_NOTIFICATION_CONFIGURATION,
  SUCCESS_NOTIFICATION_CONFIGURATION,
  WARNING_NOTIFICATION_CONFIGURATION,
} from '../constants/notification-configurations';
import {
  DEFAULT_FORBIDDEN_NOTIFICATION,
  FORBIDDEN_NOTIFICATION_MAP,
  ForbiddenReason,
} from '../models/forbidden-reason.model';
import { GraphQLErrorCollection } from '../services/graphql.service';

const NOTIFICATION_TYPE_HEADER = 'x-semeris-notification-type';
const NOTIFICATION_TEXT_HEADER = 'x-semeris-notification-text';

const NOTIFICATION_CONFIG_BY_TYPE = {
  SUCCESS: SUCCESS_NOTIFICATION_CONFIGURATION,
  WARNING: WARNING_NOTIFICATION_CONFIGURATION,
  ERROR: FAIL_NOTIFICATION_CONFIGURATION,
  INFO: { verticalPosition: 'top', duration: 4_000 },
} as const;

@Injectable()
export class ApiRequestInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private readonly store = inject<Store<RootState>>(Store);

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return (next.handle(req) as Observable<HttpResponse<any>>).pipe(
      map((response: HttpResponse<any>) => {
        if (!!response && response.type === HttpEventType.Response) {
          this.dispatchHeaderNotification(response);
        }

        if (
          !!response &&
          response.type === HttpEventType.Response &&
          response.status === 200 &&
          response.body &&
          Array.isArray(response.body.errors)
        ) {
          const errors = response.body.errors as GraphQLError[];

          const forbidden = errors.find((e) => e?.extensions?.code === 'FORBIDDEN');

          if (forbidden) {
            this.dispatchForbiddenNotification(forbidden);
          }

          const redirectError = errors.find((e) => e?.extensions?.code === 'REDIRECT');

          if (redirectError) {
            window.location.href = redirectError.message;
          }
        }

        return response;
      }),
      timeout(environment.networkTimeout),
      catchError((error: HttpErrorResponse | TimeoutError | GraphQLErrorCollection) => {
        if (error) {
          let status: number;

          if (error instanceof GraphQLErrorCollection) {
            const customError = error.errors?.find((e) => e?.extensions?.code === 'FORBIDDEN');

            if (customError) {
              status = 403;
            }

            const redirectError = error.errors?.find((e) => e?.extensions?.code === 'REDIRECT');

            if (redirectError) {
              window.location.href = redirectError.message;
            }
          }

          if (this.isTimeoutError(error)) {
            status = 408;
          }

          if (!status) {
            status = (error as HttpErrorResponse).status;
          }

          switch (status) {
            case 0:
              this.store.dispatch(AppActions.EnterOfflineMode());
              break;

            case 401:
              localStorage.removeItem('semeris-user-email');
              localStorage.removeItem('semeris-user-display-name');
              this.authService
                .logout({
                  logoutParams: {
                    returnTo: `${window.origin}/auth/logout`,
                  },
                })
                .subscribe();
              break;

            case 403:
              this.dispatchForbiddenNotification(
                error instanceof GraphQLErrorCollection
                  ? error.errors?.find((e) => e?.extensions?.code === 'FORBIDDEN')
                  : (error as HttpErrorResponse).error
              );
              break;

            case 408:
              this.store.dispatch(
                AppActions.PushNotification({
                  message: 'The network appears to be down. Please try again shortly.',
                  action: 'Reload application',
                  id: 'reload_application',
                  closable: true,
                  config: {
                    panelClass: 'warning-notification',
                    verticalPosition: 'top',
                  },
                })
              );
              break;
          }
        }

        return throwError(error);
      })
    );
  }

  private dispatchForbiddenNotification(errorSource?: GraphQLError | { reason?: string; detail?: string }): void {
    const ext = (errorSource as any)?.extensions;
    const reason =
      ext?.reason ?? ext?.exception?.response?.reason ?? ext?.response?.reason ?? (errorSource as any)?.reason;
    const config =
      reason && FORBIDDEN_NOTIFICATION_MAP[reason as ForbiddenReason]
        ? FORBIDDEN_NOTIFICATION_MAP[reason as ForbiddenReason]
        : DEFAULT_FORBIDDEN_NOTIFICATION;

    this.store.dispatch(
      AppActions.PushNotification({
        message: config.message,
        action: config.action,
        id: config.id,
        closable: config.closable,
        config: {
          verticalPosition: 'top',
        },
      })
    );
  }

  private isTimeoutError(error: unknown): error is TimeoutError {
    return error instanceof TimeoutError;
  }

  private dispatchHeaderNotification(response: HttpResponse<any>): void {
    const type = response.headers?.get(NOTIFICATION_TYPE_HEADER) as keyof typeof NOTIFICATION_CONFIG_BY_TYPE | null;
    const rawText = response.headers?.get(NOTIFICATION_TEXT_HEADER);

    if (!type || !rawText || !(type in NOTIFICATION_CONFIG_BY_TYPE)) {
      return;
    }

    this.store.dispatch(
      AppActions.PushNotification({
        message: decodeURIComponent(rawText),
        config: NOTIFICATION_CONFIG_BY_TYPE[type],
      })
    );
  }
}
